import type { NeedKind } from "@station-keeping/sim";
import { memo, useEffect, useRef, useState } from "react";
import { layoutText } from "../pixel/font";
import { NEED_ICONS, UI_ICONS, bitmapRuns, type UiIconName } from "../pixel/icons";

/**
 * Le pixel de l'interface : deux pixels d'écran ronds (`--px`), et le pixel des sprites (`--art`, et
 * `--art-sm` pour les listes), un nombre entier de pixels d'écran. Ainsi les biseaux, la police bitmap et les vignettes restent nets à toute
 * densité (1, 1,25, 1,5, 2, 3…). Appelé au démarrage, puis à chaque changement d'écran.
 */
export function installPixelUnits(): void {
  const apply = () => {
    const ratio = window.devicePixelRatio || 1;
    const root = document.documentElement.style;
    root.setProperty("--px", `${Math.max(1, Math.round(2 * ratio)) / ratio}px`);
    root.setProperty("--art", `${Math.max(1, Math.round(ratio)) / ratio}px`);
    // Vignettes des listes : à peu près moitié moins grandes, toujours un nombre entier de pixels d'écran.
    root.setProperty("--art-sm", `${Math.max(1, Math.ceil(ratio / 2)) / ratio}px`);
    window.matchMedia(`(resolution: ${ratio}dppx)`).addEventListener("change", apply, { once: true });
  };
  apply();
}

/** Tracé d'un ensemble de runs [x, y, longueur] : un seul chemin SVG. */
function pathOf(runs: readonly (readonly [number, number, number])[]): string {
  let d = "";
  for (const [x, y, length] of runs) d += `M${x} ${y}h${length}v1h${-length}z`;
  return d;
}

function PixelSvg({
  width,
  height,
  d,
  scale,
  className,
}: {
  width: number;
  height: number;
  d: string;
  scale: number;
  className?: string | undefined;
}) {
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      focusable="false"
      shapeRendering="crispEdges"
      className={`pixel-svg ${className ?? ""}`}
      style={{ width: `calc(var(--px) * ${width * scale})`, height: `calc(var(--px) * ${height * scale})` }}
    >
      <path d={d} fill="currentColor" />
    </svg>
  );
}

/**
 * Texte en police bitmap (scores, titres, comptes à rebours). Les lecteurs d'écran lisent `label` (le texte
 * lui-même par défaut) ; `label={false}` quand le texte est déjà dit à côté.
 */
export const PixelText = memo(function PixelText({
  text,
  scale = 1,
  className,
  label,
}: {
  text: string;
  scale?: number;
  className?: string;
  label?: string | false;
}) {
  const bitmap = layoutText(text);
  return (
    <span className={`pixel-text ${className ?? ""}`}>
      {label === false ? null : <span className="sr-only">{label ?? text}</span>}
      <PixelSvg width={bitmap.width} height={bitmap.height} d={pathOf(bitmap.runs)} scale={scale} />
    </span>
  );
});

const iconPaths = new Map<string, string>();

function iconPath(key: string, bitmap: readonly string[]): string {
  let d = iconPaths.get(key);
  if (d === undefined) {
    d = pathOf(bitmapRuns(bitmap));
    iconPaths.set(key, d);
  }
  return d;
}

/** Pictogramme pixel de l'interface (12 × 12), décoratif : le texte voisin dit ce qu'il signifie. */
export function Icon({ name, scale = 1, className }: { name: UiIconName; scale?: number; className?: string }) {
  return <PixelSvg width={12} height={12} d={iconPath(name, UI_ICONS[name])} scale={scale} className={className} />;
}

/** Pictogramme d'un besoin de vaisseau (10 × 10), les mêmes que sur le plan. */
export function NeedIcon({ kind, scale = 1, className }: { kind: NeedKind; scale?: number; className?: string }) {
  return <PixelSvg width={10} height={10} d={iconPath(`need-${kind}`, NEED_ICONS[kind])} scale={scale} className={className} />;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Compteur qui fait rouler ses chiffres par paliers vers sa nouvelle valeur (crédits). Sans animation si le
 * joueur la refuse.
 */
export function RollingNumber({
  value,
  format,
  scale = 1,
  className,
  label,
}: {
  value: number;
  format: (value: number) => string;
  scale?: number;
  className?: string;
  label: string;
}) {
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  useEffect(() => {
    if (prefersReducedMotion() || Math.abs(value - shownRef.current) <= 1) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const steps = 8;
    const stride = Math.max(1, Math.ceil(Math.abs(value - shownRef.current) / steps));
    const timer = setInterval(() => {
      const current = shownRef.current;
      const next = current < value ? Math.min(value, current + stride) : Math.max(value, current - stride);
      shownRef.current = next;
      setShown(next);
      if (next === value) clearInterval(timer);
    }, 45);
    return () => clearInterval(timer);
  }, [value]);
  return <PixelText text={format(shown)} scale={scale} className={className} label={label} />;
}
